import React, { useEffect, useState } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Button,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Assignment } from '@mui/icons-material';
import { UserUtils } from '../../types';

import TitleBar from '../../components/TitleBar/TitleBar';
import { formatDisplayDate } from '../../utils/dateUtils';
import { getUser } from '../../services/auth';
import { 
    getRevieweeClosedQuestionnaireResponses,
    getReviewerClosedQuestionnaireResponses,
    type QuestionnaireResponse
} from '../../services/api';

const ClosedReviews: React.FC = () => {
    const [closedReviews, setClosedReviews] = useState<QuestionnaireResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isReviewer, setIsReviewer] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const user = getUser();
        if (user) {
            setIsReviewer(UserUtils.isReviewer(user));
        }
    }, []);

    useEffect(() => {
        const fetchClosedReviews = async () => {
            setLoading(true);
            try {
                const response = isReviewer 
                    ? await getReviewerClosedQuestionnaireResponses()
                    : await getRevieweeClosedQuestionnaireResponses();
                setClosedReviews(response.data || []);
                setError('');
            } catch (err) {
                console.error('Error fetching closed reviews:', err);
                setError('Failed to load closed reviews');
                setClosedReviews([]);
            } finally {
                setLoading(false);
            }
        };

        fetchClosedReviews();
    }, [isReviewer]);

    const handleViewAnswers = (review: QuestionnaireResponse) => {
        const reviewEntityTypeId = review.review_entity_type_id || 
            (typeof review.review_entity_type_name === 'object' ? review.review_entity_type_name?.id : '');
        navigate(`/review-answers/${review.id}/${reviewEntityTypeId}`);
    };

    const renderReviewStatus = (review: QuestionnaireResponse) => {
        const overdueText = review.is_submission_overdue
            ? `Submission overdue${review.days_past_submission_due != null ? ` by ${review.days_past_submission_due}d` : ''}`
            : review.is_closure_overdue
                ? `Closure overdue${review.days_past_closure_due != null ? ` by ${review.days_past_closure_due}d` : ''}`
                : '';
        const overdueDueDate = review.is_submission_overdue
            ? review.submission_due_by
            : review.is_closure_overdue
                ? review.closure_due_by
                : null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip
                    label={review.review_status}
                    size="small"
                    color={
                        review.review_status === 'SUBMITTED' ? 'success' :
                        review.review_status === 'CLOSED' ? 'default' :
                        review.review_status === 'CANCELLED' ? 'error' :
                        'default'
                    }
                />
                {review.is_overdue && (
                    <Chip
                        label={overdueText}
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                )}
                {review.is_overdue && overdueDueDate && (
                    <Typography variant="caption" color="error">
                        Due by {formatDisplayDate(overdueDueDate)}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <>
            <TitleBar />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Back Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </Button>
                </Box>

                <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                        Closed Reviews
                    </Typography>
                    
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Typography color="error" sx={{ my: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && closedReviews.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No closed reviews found.
                        </Typography>
                    )}

                    {!loading && !error && closedReviews.length > 0 && (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Name</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity Type</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Date</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Status</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Reviewer</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Reviewee</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontWeight: 'bold' }}>Comments</Typography></TableCell>
                                        <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {closedReviews.map((review) => (
                                        <TableRow key={review.id}>
                                            <TableCell>{review.review_name}</TableCell>
                                            <TableCell>{review.review_entity_name}</TableCell>
                                            <TableCell>
                                                {typeof review.review_entity_type_name === 'string' 
                                                    ? review.review_entity_type_name 
                                                    : review.review_entity_type_name?.display_label || 'N/A'}
                                            </TableCell>
                                            <TableCell>{review.review_date}</TableCell>
                                            <TableCell>
                                                {renderReviewStatus(review)}
                                            </TableCell>
                                            <TableCell>{review.reviewer_name}</TableCell>
                                            <TableCell>{review.reviewee_name}</TableCell>
                                            <TableCell>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        maxWidth: 200, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {review.review_comments || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Answers">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleViewAnswers(review)}
                                                        size="small"
                                                    >
                                                        <Assignment />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Container>
        </>
    );
};

export default ClosedReviews;
